import { getSidoCentroid, getSigunguCentroid } from "./centroid";
import { SIDO_ALIASES } from "./aliases";
import { applySuccession, SIGUNGU } from "./succession";

export type CaseGeo = Readonly<{
  caseId: string;
  sigunguCode: string;
  lon?: number | null;
  lat?: number | null;
}>;

export type ClusterLevel = "sido" | "sigungu" | "case";

export type Cluster = Readonly<{
  key: string;
  level: ClusterLevel;
  count: number;
  label: string;
  lon: number;
  lat: number;
  caseIds: readonly string[];
}>;

type NormalizedCase = CaseGeo & {
  currentSigunguCode: string;
  currentSidoCode: string;
};

const sidoNameByCode = new Map(SIDO_ALIASES.map((region) => [region.code, region.name]));
const sigunguNameByCode = new Map(SIGUNGU.map((region) => [region.code, region.name]));

function normalizeCase(item: CaseGeo): NormalizedCase {
  if (!item.caseId) throw new Error("caseId is required for clustering");
  const currentSigunguCode = applySuccession(item.sigunguCode);
  getSigunguCentroid(currentSigunguCode);
  return {
    ...item,
    currentSigunguCode,
    currentSidoCode: currentSigunguCode.slice(0, 2),
  };
}

function aggregate(
  cases: readonly NormalizedCase[],
  level: Exclude<ClusterLevel, "case">,
): Cluster[] {
  const groups = new Map<string, string[]>();
  for (const item of cases) {
    const key = level === "sido" ? item.currentSidoCode : item.currentSigunguCode;
    const caseIds = groups.get(key) ?? [];
    caseIds.push(item.caseId);
    groups.set(key, caseIds);
  }

  return [...groups].map(([key, caseIds]) => {
    caseIds.sort((a, b) => a.localeCompare(b));
    const [lon, lat] = level === "sido" ? getSidoCentroid(key) : getSigunguCentroid(key);
    return Object.freeze({
      key,
      level,
      count: caseIds.length,
      label: level === "sido" ? (sidoNameByCode.get(key) ?? "지역") : (sigunguNameByCode.get(key) ?? "시군구"),
      lon,
      lat,
      caseIds: Object.freeze(caseIds),
    });
  });
}

function fnv1a(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function jitter(caseId: string, lon: number, lat: number): readonly [number, number] {
  const hash = fnv1a(caseId);
  const angle = ((hash % 3_600) / 3_600) * Math.PI * 2;
  const radiusMeters = (hash >>> 12) % 121;
  const latOffset = (Math.sin(angle) * radiusMeters) / 111_320;
  const longitudeMetersPerDegree = Math.max(1, 111_320 * Math.cos((lat * Math.PI) / 180));
  const lonOffset = (Math.cos(angle) * radiusMeters) / longitudeMetersPerDegree;
  return [lon + lonOffset, lat + latOffset];
}

function individual(cases: readonly NormalizedCase[]): Cluster[] {
  const positioned = cases.map((item) => {
    const fallback = getSigunguCentroid(item.currentSigunguCode);
    const lon = Number.isFinite(item.lon) ? (item.lon as number) : fallback[0];
    const lat = Number.isFinite(item.lat) ? (item.lat as number) : fallback[1];
    return { item, lon, lat };
  });
  const coordinateCounts = new Map<string, number>();
  for (const item of positioned) {
    const key = `${item.lon}:${item.lat}`;
    coordinateCounts.set(key, (coordinateCounts.get(key) ?? 0) + 1);
  }

  return positioned
    .sort((a, b) => a.item.caseId.localeCompare(b.item.caseId))
    .map(({ item, lon, lat }) => {
      const duplicated = (coordinateCounts.get(`${lon}:${lat}`) ?? 0) > 1;
      const [displayLon, displayLat] = duplicated ? jitter(item.caseId, lon, lat) : [lon, lat];
      return Object.freeze({
        key: item.caseId,
        level: "case" as const,
        count: 1,
        label: "공개 정보",
        lon: displayLon,
        lat: displayLat,
        caseIds: Object.freeze([item.caseId]),
      });
    });
}

export function clusterByZoom(cases: readonly CaseGeo[], zoom: number): Cluster[] {
  if (!Number.isFinite(zoom)) throw new Error("zoom must be finite");
  const normalized = cases.map(normalizeCase);
  const clusters = zoom < 7
    ? aggregate(normalized, "sido")
    : zoom < 10
      ? aggregate(normalized, "sigungu")
      : individual(normalized);
  const total = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  if (total !== cases.length) {
    throw new Error(`cluster count invariant failed: ${total} !== ${cases.length}`);
  }
  return clusters;
}
